import { useState } from 'react';
import {
  patchProfile,
  requestEmailChange,
  type Me,
  type ProfilePatch,
} from '../../api/endpoints.js';
import { useAction } from '../../api/hooks.js';
import { goStep } from '../../flow/nav.js';
import { Failure } from '../../ui/kit.js';
import {
  FlowField,
  FlowScreen,
  FlowSub,
  FlowTitle,
  IconCard,
  Pill,
  PrimaryButton,
} from '../../ui/flow.js';

/**
 * Mobile screens 11–12: the profile form and the facial-signature intro.
 *
 * One deliberate addition to the mobile form: DATE OF BIRTH. This backend's
 * consent service refuses a submission for a subject with no DOB
 * (DOB_REQUIRED) because the DOB decides who must sign — so collecting it here
 * is what makes the consent screen two steps later reachable. It is write-once
 * on the api, and the label says so.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOB_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function ProfileStep({ me, reloadMe }: { me: Me; reloadMe: () => void }) {
  const [firstName, setFirstName] = useState(me.person.first_name ?? '');
  const [lastName, setLastName] = useState(me.person.last_name ?? '');
  // Prefill from the address already awaiting its link, else the verified one,
  // so returning to this step does not re-prompt for an address already sent.
  const knownEmail = me.person.pending_email ?? me.person.email;
  const [email, setEmail] = useState(knownEmail ?? '');
  const [dob, setDob] = useState(me.person.date_of_birth ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const dobLocked = me.person.date_of_birth !== null;

  const save = useAction(
    async () => {
      const patch: ProfilePatch = {};
      if (firstName.trim() !== (me.person.first_name ?? '')) patch.first_name = firstName.trim();
      if (lastName.trim() !== (me.person.last_name ?? '')) patch.last_name = lastName.trim();
      if (!dobLocked && dob.trim().length > 0) patch.date_of_birth = dob.trim();
      if (Object.keys(patch).length > 0) await patchProfile(patch);
      // The email route is separate: a change restarts verification, which is
      // a dashboard action later, never a blocker here.
      if (email.trim() !== (knownEmail ?? '')) await requestEmailChange(email.trim());
    },
    () => {
      reloadMe();
      goStep('face-intro', { replace: true });
    },
  );

  const incomplete =
    firstName.trim().length === 0 ||
    lastName.trim().length === 0 ||
    email.trim().length === 0 ||
    (!dobLocked && dob.trim().length === 0);

  const submit = () => {
    if (incomplete) {
      setProblem('First Name, Last Name, Email, and Date of Birth are required.');
      return;
    }
    if (!EMAIL_PATTERN.test(email.trim())) {
      setProblem('Please enter a valid email address.');
      return;
    }
    if (!dobLocked && !DOB_PATTERN.test(dob.trim())) {
      setProblem('Please enter the date of birth as YYYY-MM-DD.');
      return;
    }
    setProblem(null);
    save.run();
  };

  return (
    <FlowScreen
      progress={70}
      onBack={() => window.history.back()}
      footer={
        <PrimaryButton onClick={submit} disabled={incomplete || save.pending}>
          {save.pending ? 'Sending…' : 'Continue'}
        </PrimaryButton>
      }
    >
      <FlowTitle>Let&rsquo;s create your ImageShield Profile</FlowTitle>
      {problem !== null && <p className="flow-error">{problem}</p>}
      <div className="field-stack">
        <FlowField
          label="First Name"
          value={firstName}
          onChange={(value) => {
            setFirstName(value);
            setProblem(null);
          }}
          placeholder="Jane"
          autoComplete="given-name"
          disabled={save.pending}
        />
        <FlowField
          label="Last Name"
          value={lastName}
          onChange={(value) => {
            setLastName(value);
            setProblem(null);
          }}
          placeholder="Doe"
          autoComplete="family-name"
          disabled={save.pending}
        />
        <FlowField
          label="Email Address"
          value={email}
          onChange={(value) => {
            setEmail(value);
            setProblem(null);
          }}
          placeholder="jane.doe@example.com"
          type="email"
          autoComplete="email"
          disabled={save.pending}
        />
        <FlowField
          label={dobLocked ? 'Date of Birth (set)' : 'Date of Birth'}
          value={dob}
          onChange={(value) => {
            setDob(value);
            setProblem(null);
          }}
          placeholder="YYYY-MM-DD"
          autoComplete="bday"
          disabled={save.pending || dobLocked}
        />
      </div>
      <p className="flow-note">
        You can verify this email later from your Dashboard. Your date of birth decides who signs
        the biometric consent and can&rsquo;t be changed afterwards.
      </p>
      {save.error !== null && (
        <Failure
          error={save.error}
          hint={
            {
              DOB_ALREADY_SET: 'The date of birth is write-once — changing it is a support path.',
              EMAIL_IN_USE: 'That email already belongs to another person.',
            }[save.error.code]
          }
        />
      )}
    </FlowScreen>
  );
}

export function FaceIntroStep() {
  return (
    <FlowScreen
      progress={74}
      onBack={() => window.history.back()}
      footer={<PrimaryButton onClick={() => goStep('consent', { replace: true })}>Continue</PrimaryButton>}
    >
      <FlowTitle>Let&rsquo;s create your facial signature</FlowTitle>
      <FlowSub>
        The next step create your facial signature so that we can monitor for the use of your
        likeness online. We&rsquo;ll need a few things from you.
      </FlowSub>
      <Pill>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="2" />
          <path d="M12 9v4l2.5 2.5M9 2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        2 min to complete
      </Pill>
      <div className="option-stack" style={{ marginTop: 20 }}>
        <IconCard
          icon={
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="#380E99" strokeWidth="1.8" />
              <circle cx="9" cy="10" r="1.2" fill="#380E99" />
              <circle cx="15" cy="10" r="1.2" fill="#380E99" />
              <path d="M8.5 15c1 1.2 2.2 1.8 3.5 1.8s2.5-.6 3.5-1.8" stroke="#380E99" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          }
          title="Live Selfie"
        >
          We&rsquo;ll scan your face so that we can create a facial signature. This will be used to
          search for the use of your likeness in online content.
        </IconCard>
        <IconCard
          icon={
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="5" width="14" height="14" rx="2.5" stroke="#380E99" strokeWidth="1.8" />
              <path d="M21 8v9a3 3 0 0 1-3 3h-9" stroke="#380E99" strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="8" cy="10" r="1.4" fill="#380E99" />
              <path d="M3.5 16.5l4-4 3.5 3.5 2.5-2.5 3.5 3.5" stroke="#380E99" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          }
          title="Your social media photos"
        >
          You&rsquo;ll upload up to 50 photos of yourself that have been shared on social media
          recently. Include your social media profile pictures. We&rsquo;ll monitor for the use and
          abuse of these photos, and your likeness, online.
        </IconCard>
      </div>
    </FlowScreen>
  );
}
